import React, { useEffect, useState } from "react";
import { api, endpoints } from "@/lib/api";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const ImpactCalculator = ({ compact = false }) => {
  const [items, setItems] = useState([]);
  const [amount, setAmount] = useState(1000);
  useEffect(() => { api.get("/impact-mappings").then(setItems).catch(() => {}); }, []);

  // pick closest mapping
  const closest = items.reduce((prev, cur) => (Math.abs(cur.amount - amount) < Math.abs((prev?.amount ?? 1e9) - amount) ? cur : prev), null);
  const multiplier = closest ? Math.max(1, Math.round(amount / closest.amount)) : 1;
  const Icon = closest ? (Icons[closest.icon] || Icons.Sparkles) : Icons.Sparkles;

  return (
    <section id="impact-calculator" className={compact ? "" : "surface-tint"}>
      <div className={`${compact ? "" : "container-wide py-14 lg:py-20"}`}>
        {!compact && <SectionHeader eyebrow="Donation Impact Calculator" title="See exactly what your donation does." lead="Slide, tap or type an amount. We’ll translate it into real, verifiable outcomes tied to our programs." />}
        <div className={`grid gap-6 ${compact ? "" : "lg:grid-cols-12"}`}>
          <div className={compact ? "" : "lg:col-span-7"}>
            <div className="glass rounded-2xl p-6 caf-shadow">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-[hsl(var(--muted-ink))]">I want to contribute</div>
                <div className="text-3xl font-extrabold text-foreground tabular">₹{Number(amount).toLocaleString("en-IN")}</div>
              </div>
              <input
                type="range" min="100" max="50000" step="100" value={amount}
                data-testid="impact-calc-slider"
                onChange={(e) => setAmount(Number(e.target.value))}
                className="mt-4 w-full accent-[hsl(var(--orange))]"
              />
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {[500, 1000, 2500, 5000, 10000, 25000].map((p) => (
                  <button key={p} type="button" data-testid={`impact-calc-preset-${p}`} onClick={() => setAmount(p)}
                    className={`h-9 px-3 rounded-full text-xs font-medium border transition-colors ${amount === p ? "bg-foreground text-background border-foreground" : "border-border text-foreground/85 hover:border-foreground"}`}>₹{p.toLocaleString("en-IN")}</button>
                ))}
                <Input type="number" min="100" data-testid="impact-calc-input" value={amount} onChange={(e) => setAmount(Math.max(0, Number(e.target.value || 0)))} className="w-28 rounded-full" />
              </div>
              <div className="mt-6 flex items-center gap-4 rounded-xl bg-background/70 border border-border p-4">
                <div className="h-12 w-12 rounded-lg bg-[hsl(var(--orange)/0.12)] text-[hsl(var(--orange))] grid place-items-center"><Icon className="h-6 w-6" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--green))]">Your impact</div>
                  <div className="heading-3 text-lg mt-0.5">{multiplier}× {closest?.unit || "program units"}</div>
                  <div className="text-xs text-[hsl(var(--muted-ink))] mt-1">{closest?.category || "General Fund"} • Verified via program vouchers</div>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/donate"><Button data-testid="impact-calc-donate-cta" className="btn-primary">Donate ₹{Number(amount).toLocaleString("en-IN")}</Button></Link>
                <Link to="/transparency"><Button variant="outline" className="rounded-full border-foreground text-foreground hover:bg-foreground hover:text-background">See where money goes</Button></Link>
              </div>
            </div>
          </div>
          {!compact && (
            <div className="lg:col-span-5">
              <Reveal>
                <div className="rounded-2xl border border-border p-6 bg-background">
                  <div className="eyebrow">Impact ladder</div>
                  <ul className="mt-3 divide-y divide-border">
                    {items.map((m) => {
                      const MIcon = Icons[m.icon] || Icons.Sparkles;
                      return (
                        <li key={m.amount} className="flex items-center gap-3 py-3">
                          <div className="h-9 w-9 rounded-md bg-[hsl(var(--surface-tint))] text-foreground grid place-items-center"><MIcon className="h-4.5 w-4.5" /></div>
                          <div className="flex-1"><div className="text-sm font-semibold text-foreground">₹{m.amount.toLocaleString("en-IN")}</div><div className="text-xs text-[hsl(var(--muted-ink))]">{m.unit}</div></div>
                          <span className="badge-green border rounded-full px-2 py-0.5 text-[10px]">{m.category}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
